/**
 * Phase 3 Diagram Renderer
 * React Flow renderer for AI-powered diagram types:
 * - System Context (C4 Level 1)
 * - Data Flow
 * - Event Storming (DDD)
 * - Bounded Context Map (DDD)
 */

import { useCallback, useMemo, useEffect, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  Edge,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { UnifiedDiagramData, UnifiedDiagramNode } from '@veodiagram/shared';
import { phase3NodeTypes, convertPhase3ToReactFlow } from './phase3';
import { applyLayout, detectBestLayout, LayoutDirection } from './shared/layoutEngine';
import { SmartBezierEdge, SmartSmoothStepEdge } from '../edges';

// Smart edge types with label positioning
const phase3EdgeTypes = {
  smartBezier: SmartBezierEdge,
  smartSmoothStep: SmartSmoothStepEdge,
};

// Import unified controls - Issue #245
import { DiagramControlsProvider, DiagramKeyboardShortcuts, useDiagramControlsOptional } from '../controls';
import type { DiagramCapabilities, FilterDefinition } from '../controls';

// ============= Helper Functions (Issue #231) =============

/**
 * Extract node type groups from Phase 3 diagram nodes
 */
function extractNodeTypeGroups(nodes: UnifiedDiagramNode[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();

  nodes.forEach(node => {
    const nodeType = node.type || 'unknown';
    if (!groups.has(nodeType)) {
      groups.set(nodeType, []);
    }
    groups.get(nodeType)!.push(node.id);
  });

  return groups;
}

/**
 * Get connected node IDs for focus mode (ego network)
 */
function getConnectedNodeIds(
  nodeId: string,
  edges: { source: string; target: string }[],
  hops: number = 1
): Set<string> {
  const connected = new Set<string>([nodeId]);

  for (let i = 0; i < hops; i++) {
    const currentNodes = Array.from(connected);
    currentNodes.forEach(nid => {
      edges.forEach(edge => {
        if (edge.source === nid) connected.add(edge.target);
        if (edge.target === nid) connected.add(edge.source);
      });
    });
  }

  return connected;
}

/**
 * Filter nodes based on search query
 */
function filterNodesBySearch(nodes: Node[], query: string): Set<string> {
  if (!query.trim()) return new Set(nodes.map(n => n.id));

  const lowerQuery = query.toLowerCase();
  const matched = new Set<string>();

  nodes.forEach(node => {
    const label = String(node.data?.label || '').toLowerCase();
    const nodeType = String(node.type || '').toLowerCase();
    if (label.includes(lowerQuery) || nodeType.includes(lowerQuery)) {
      matched.add(node.id);
    }
  });

  return matched;
}

// ============= DiagramToolbar Component (Issue #231) =============

interface DiagramToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  focusedNodeId: string | null;
  onClearFocus: () => void;
  focusHops: number;
  onFocusHopsChange: (hops: number) => void;
  nodeTypeGroups: Map<string, string[]>;
  selectedNodeType: string | null;
  onNodeTypeChange: (type: string | null) => void;
  layoutDirection: LayoutDirection;
  onLayoutDirectionChange: (dir: LayoutDirection) => void;
  onAutoLayout: () => void;
  onFitView: () => void;
  isLayouting: boolean;
  totalNodes: number;
  visibleNodes: number;
}

function DiagramToolbar({
  searchQuery,
  onSearchChange,
  focusedNodeId,
  onClearFocus,
  focusHops,
  onFocusHopsChange,
  nodeTypeGroups,
  selectedNodeType,
  onNodeTypeChange,
  layoutDirection,
  onLayoutDirectionChange,
  onAutoLayout,
  onFitView,
  isLayouting,
  totalNodes,
  visibleNodes,
}: DiagramToolbarProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Diagram Controls
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {visibleNodes}/{totalNodes} nodes
          </span>
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Search Nodes
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name or type..."
              className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400"
            />
          </div>

          {/* Focus Mode */}
          {focusedNodeId && (
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  Focus Mode Active
                </span>
                <button
                  onClick={onClearFocus}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-600 dark:text-blue-400">Hops:</label>
                <select
                  value={focusHops}
                  onChange={(e) => onFocusHopsChange(Number(e.target.value))}
                  className="text-xs px-1.5 py-0.5 border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-slate-800"
                >
                  <option value={1}>1 (direct)</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </div>
            </div>
          )}

          {/* Node Type Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Filter by Type
            </label>
            <select
              value={selectedNodeType || ''}
              onChange={(e) => onNodeTypeChange(e.target.value || null)}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="">All types</option>
              {Array.from(nodeTypeGroups.entries())
                .sort((a, b) => b[1].length - a[1].length)
                .map(([type, nodeIds]) => (
                  <option key={type} value={type}>
                    {type} ({nodeIds.length})
                  </option>
                ))}
            </select>
          </div>

          {/* Layout Direction */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Layout Direction
            </label>
            <div className="flex gap-1">
              {(['TB', 'LR', 'BT', 'RL'] as LayoutDirection[]).map((dir) => (
                <button
                  key={dir}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.warn('!'.repeat(80));
                    console.warn('!'.repeat(80));
                    onLayoutDirectionChange(dir);
                  }}
                  className={`flex-1 px-2 py-1 text-xs rounded ${
                    layoutDirection === dir
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                  title={
                    dir === 'TB' ? 'Top to Bottom' :
                    dir === 'LR' ? 'Left to Right' :
                    dir === 'BT' ? 'Bottom to Top' :
                    'Right to Left'
                  }
                >
                  {dir}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={onAutoLayout}
              disabled={isLayouting}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLayouting ? 'Layouting...' : 'Auto Layout'}
            </button>
            <button
              onClick={onFitView}
              className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600"
            >
              Fit View
            </button>
          </div>

          {/* Help Text */}
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">
            Tip: Double-click a node to focus on its connections
          </p>
        </div>
      )}
    </div>
  );
}

// ============= Types =============

export type Phase3DiagramType = 'systemContext';

interface Phase3DiagramRendererProps {
  data: UnifiedDiagramData;
  diagramType: Phase3DiagramType;
  className?: string;
  readOnly?: boolean;
  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge) => void;
}

export interface Phase3DiagramRendererHandle {
  fitView: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  getViewportElement: () => HTMLElement | null;
  applyAutoLayout: () => Promise<void>;
}

// ============= Component =============

function Phase3DiagramRendererInner(
  {
    data,
    diagramType,
    className = '',
    readOnly = true,
    onNodeClick,
    onEdgeClick,
  }: Phase3DiagramRendererProps,
  ref: React.Ref<Phase3DiagramRendererHandle>
) {
  // DEBUG #252: Log when component renders

  const reactFlowInstance = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);

  // Issue #215: Auto layout state
  const [isLayouting, setIsLayouting] = useState(false);
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('TB');
  const isFirstRender = useRef(true); // Track first render to skip initial auto-layout
  const applyAutoLayoutRef = useRef<(() => Promise<void>) | null>(null); // Issue #252: Ref to hold latest applyAutoLayout

  // Issue #231: Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNodeType, setSelectedNodeType] = useState<string | null>(null);

  // Issue #231: Focus mode state
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [focusHops, setFocusHops] = useState(1);

  // Issue #252: Get context's layoutDirection and sync with local state
  const controls = useDiagramControlsOptional();
  const contextLayoutDirection = controls?.layoutDirection;

  // Issue #252: Track previous context direction to detect changes
  const prevContextDirectionRef = useRef<LayoutDirection | undefined>(undefined);

  // Issue #252: Sync context's layoutDirection to local state when it changes
  useEffect(() => {
    // Only sync if context direction exists and has actually changed
    if (contextLayoutDirection && contextLayoutDirection !== prevContextDirectionRef.current) {
      prevContextDirectionRef.current = contextLayoutDirection;

      // Only update local state if it differs from context
      if (contextLayoutDirection !== layoutDirection) {
        setLayoutDirection(contextLayoutDirection);
      }
    }
  }, [contextLayoutDirection, layoutDirection]);

  // Convert diagram data to React Flow format
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => convertPhase3ToReactFlow(data, diagramType),
    [data, diagramType]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Issue #231: Extract node type groups
  const nodeTypeGroups = useMemo(() => extractNodeTypeGroups(data.nodes), [data.nodes]);

  // Issue #231: Calculate visible nodes based on filters
  const visibleNodeIds = useMemo(() => {
    let visible = new Set(data.nodes.map(n => n.id));

    // Apply search filter
    if (searchQuery.trim()) {
      const searchMatches = filterNodesBySearch(nodes, searchQuery);
      visible = new Set([...visible].filter(id => searchMatches.has(id)));
    }

    // Apply node type filter
    if (selectedNodeType) {
      const typeNodes = nodeTypeGroups.get(selectedNodeType) || [];
      visible = new Set([...visible].filter(id => typeNodes.includes(id)));
    }

    // Apply focus mode
    if (focusedNodeId) {
      const focusedNodes = getConnectedNodeIds(focusedNodeId, data.edges, focusHops);
      visible = new Set([...visible].filter(id => focusedNodes.has(id)));
    }

    return visible;
  }, [data.nodes, data.edges, searchQuery, selectedNodeType, focusedNodeId, focusHops, nodes, nodeTypeGroups]);

  // Issue #231: Filter nodes and edges for display
  const { filteredNodes, filteredEdges } = useMemo(() => {
    const filteredNodes = nodes.map(node => ({
      ...node,
      hidden: !visibleNodeIds.has(node.id),
      style: {
        ...node.style,
        opacity: visibleNodeIds.has(node.id) ? 1 : 0.15,
      },
    }));

    const filteredEdges = edges.map(edge => ({
      ...edge,
      hidden: !visibleNodeIds.has(edge.source) || !visibleNodeIds.has(edge.target),
      style: {
        ...edge.style,
        opacity: (visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)) ? 1 : 0.1,
      },
    }));

    return { filteredNodes, filteredEdges };
  }, [nodes, edges, visibleNodeIds]);

  // Update nodes and edges when data changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = convertPhase3ToReactFlow(data, diagramType);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [data, diagramType, setNodes, setEdges]);

  // Handle new connections
  const onConnect = useCallback(
    (connection: Connection) => {
      if (readOnly) return;
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges, readOnly]
  );

  // Handle node click
  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeClick?.(node);
    },
    [onNodeClick]
  );

  // Issue #231: Handle node double-click for focus mode
  const handleNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (focusedNodeId === node.id) {
        // Toggle off if already focused
        setFocusedNodeId(null);
      } else {
        setFocusedNodeId(node.id);
      }
      // Zoom to the node
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.3,
          nodes: [node],
          duration: 300,
        });
      }, 50);
    },
    [focusedNodeId, reactFlowInstance]
  );

  // Handle edge click
  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      onEdgeClick?.(edge);
    },
    [onEdgeClick]
  );

  // Issue #231: Clear focus mode
  const clearFocus = useCallback(() => {
    setFocusedNodeId(null);
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2 });
    }, 50);
  }, [reactFlowInstance]);

  // Issue #252: Handler that syncs layout direction to both local and context state
  // The layout is automatically applied by the useEffect that watches layoutDirection
  const handleLayoutDirectionChange = useCallback((direction: LayoutDirection) => {
    console.log('='.repeat(60));
    console.log('='.repeat(60));

    // Skip if same direction (no change needed)
    if (direction === layoutDirection) {
      return;
    }

    // Update local state - this will trigger the useEffect to apply layout
    setLayoutDirection(direction);

    // Also update context if available
    if (controls?.setLayoutDirection) {
      controls.setLayoutDirection(direction);
    } else {
    }
  }, [controls, layoutDirection]);

  // Issue #215, #231: Apply auto layout to diagram with direction
  const applyAutoLayout = useCallback(async () => {
    console.log('-'.repeat(60));
    console.log('-'.repeat(60));

    setIsLayouting(true);
    try {
      // Detect best layout algorithm based on diagram structure
      const algorithm = detectBestLayout(data.nodes, data.edges);

      // Build metadata for layout engine with direction
      const layoutMetadata = {
        ...data.metadata,
        layoutAlgorithm: algorithm,
        layoutDirection: layoutDirection,
      };

      // Apply layout using unified data format
      const layoutedNodes = await applyLayout(data.nodes, data.edges, layoutMetadata);

      // Convert layouted nodes back to React Flow format
      const { nodes: newNodes, edges: newEdges } = convertPhase3ToReactFlow(
        { ...data, nodes: layoutedNodes },
        diagramType
      );

      // Update React Flow state with new positions
      setNodes(newNodes);
      setEdges(newEdges);

      // Fit view after layout
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
      }, 50);

    } catch (error) {
    } finally {
      setIsLayouting(false);
    }
  }, [data, diagramType, layoutDirection, setNodes, setEdges, reactFlowInstance]);

  // Issue #252: Keep the ref updated with the latest applyAutoLayout function
  useEffect(() => {
    applyAutoLayoutRef.current = applyAutoLayout;
  }, [applyAutoLayout]);

  // Issue #252: Auto-apply layout when direction changes
  useEffect(() => {
    console.log('*'.repeat(60));
    console.log('*'.repeat(60));

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Trigger layout when direction changes (not on first render)
    // Use the ref to get the latest applyAutoLayout function
    if (applyAutoLayoutRef.current) {
      applyAutoLayoutRef.current();
    } else {
    }
  }, [layoutDirection]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    fitView: () => reactFlowInstance.fitView({ padding: 0.2 }),
    zoomIn: () => reactFlowInstance.zoomIn(),
    zoomOut: () => reactFlowInstance.zoomOut(),
    getViewportElement: () =>
      containerRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null,
    applyAutoLayout, // Issue #215
  }));

  // Note: getPhase3DiagramLabel is now only used in getLegendItems

  // Get minimap node color based on node type
  const getMinimapNodeColor = (node: Node) => {
    const colorMap: Record<string, string> = {
      person: '#08427b',
      softwareSystem: '#1168bd',
      externalSystem: '#999999',
    };
    return colorMap[node.type || ''] || '#64748b';
  };

  return (
    <div ref={containerRef} className={`h-full w-full ${className}`}>
      <ReactFlow
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        nodeTypes={phase3NodeTypes}
        edgeTypes={phase3EdgeTypes}
        defaultEdgeOptions={{
          type: 'smartSmoothStep',
        }}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={true}
        className="bg-slate-50 dark:bg-slate-900"
      >
        <Background variant={BackgroundVariant.Dots} gap={15} size={1} />
        <Controls showInteractive={!readOnly} />
        <MiniMap
          nodeStrokeWidth={3}
          nodeColor={getMinimapNodeColor}
          zoomable
          pannable
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md"
        />

        {/* Issue #231: DiagramToolbar with search, filter, focus mode, layout direction */}
        <Panel position="top-right" className="m-2 max-w-xs">
          <DiagramToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            focusedNodeId={focusedNodeId}
            onClearFocus={clearFocus}
            focusHops={focusHops}
            onFocusHopsChange={setFocusHops}
            nodeTypeGroups={nodeTypeGroups}
            selectedNodeType={selectedNodeType}
            onNodeTypeChange={setSelectedNodeType}
            layoutDirection={layoutDirection}
            onLayoutDirectionChange={handleLayoutDirectionChange}
            onAutoLayout={applyAutoLayout}
            onFitView={() => reactFlowInstance.fitView({ padding: 0.2 })}
            isLayouting={isLayouting}
            totalNodes={nodes.length}
            visibleNodes={visibleNodeIds.size}
          />
        </Panel>

        {/* Legend Panel */}
        <Panel position="bottom-left" className="bg-white/90 dark:bg-slate-800/90 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {getLegendItems(diagramType)}
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
}

/**
 * Get legend items based on diagram type
 */
function getLegendItems(diagramType: Phase3DiagramType): React.ReactNode {
  const legends: Record<Phase3DiagramType, React.ReactNode> = {
    systemContext: (
      <div className="flex gap-3">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#08427b]"></span> Person</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#1168bd]"></span> System</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#999999]"></span> External</span>
      </div>
    ),
  };

  return legends[diagramType] || null;
}

// Wrap with forwardRef
const Phase3DiagramRendererWithRef = forwardRef(Phase3DiagramRendererInner);

// ============= Unified Controls Integration (Issue #245) =============

/**
 * Build filter definitions for Phase 3 diagram node types
 * Issue #245: Standardize Diagram Controls Across All Renderers
 */
function buildPhase3FilterDefinitions(
  nodeTypeGroups: Map<string, string[]>
): FilterDefinition[] {
  if (nodeTypeGroups.size === 0) return [];

  return [{
    id: 'nodeType',
    label: 'Type',
    type: 'dropdown',
    options: Array.from(nodeTypeGroups.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([type, ids]) => ({
        value: type,
        label: `${type} (${ids.length})`,
      })),
    getNodeValue: (node: Node) => node.type || 'unknown',
  }];
}

/**
 * Get capabilities for Phase 3 diagram types
 * Issue #245: Standardize Diagram Controls Across All Renderers
 */
function getPhase3Capabilities(
  _diagramType: Phase3DiagramType, // Reserved for future type-specific capabilities
  filters: FilterDefinition[]
): DiagramCapabilities {
  return {
    search: true, // Phase 3 has search
    focus: true, // Phase 3 has focus mode (double-click)
    filters,
    layoutDirection: true, // Phase 3 supports TB, LR, BT, RL
    autoLayout: true,
    legend: true,
    interactive: true,
    zoomPan: true,
  };
}

/**
 * Wrapper component that integrates DiagramControlsProvider
 * Issue #245: Standardize Diagram Controls Across All Renderers
 */
const Phase3DiagramRendererWithControls = forwardRef<
  Phase3DiagramRendererHandle,
  Phase3DiagramRendererProps
>((props, ref) => {
  const { data, diagramType } = props;
  const internalRef = useRef<Phase3DiagramRendererHandle>(null);

  // Forward ref
  useImperativeHandle(ref, () => ({
    fitView: () => internalRef.current?.fitView(),
    zoomIn: () => internalRef.current?.zoomIn(),
    zoomOut: () => internalRef.current?.zoomOut(),
    getViewportElement: () => internalRef.current?.getViewportElement() ?? null,
    applyAutoLayout: async () => internalRef.current?.applyAutoLayout(),
  }));

  // Build node type groups for filters
  const nodeTypeGroups = useMemo(() => extractNodeTypeGroups(data.nodes), [data.nodes]);

  // Build filter definitions
  const filterDefinitions = useMemo(
    () => buildPhase3FilterDefinitions(nodeTypeGroups),
    [nodeTypeGroups]
  );

  // Build capabilities
  const capabilities = useMemo(
    () => getPhase3Capabilities(diagramType, filterDefinitions),
    [diagramType, filterDefinitions]
  );

  return (
    <ReactFlowProvider>
      <DiagramControlsProvider
        initialCapabilities={capabilities}
        onFitView={() => internalRef.current?.fitView()}
        onZoomIn={() => internalRef.current?.zoomIn()}
        onZoomOut={() => internalRef.current?.zoomOut()}
        onAutoLayout={async () => internalRef.current?.applyAutoLayout()}
      >
        <DiagramKeyboardShortcuts />
        <Phase3DiagramRendererWithRef {...props} ref={internalRef} />
      </DiagramControlsProvider>
    </ReactFlowProvider>
  );
});

Phase3DiagramRendererWithControls.displayName = 'Phase3DiagramRendererWithControls';

// Export the wrapped version
export const Phase3DiagramRenderer = Phase3DiagramRendererWithControls;

/**
 * Check if a diagram type is a Phase 3 type
 */
export function isPhase3DiagramType(type: string): type is Phase3DiagramType {
  const phase3Types = new Set(['systemContext']);

  // Sanitize the input: remove any trailing ')' or other non-alphanumeric chars
  const sanitizedType = type?.replace(/[^a-zA-Z0-9]/g, '') || '';

  return phase3Types.has(sanitizedType) || phase3Types.has(type);
}
